import { useState } from "react";
import type { ColorCrosswalkCard } from "@/lib/manufacturing/colorCrosswalkPropose";
import {
  colorCrosswalkCardsToCsv,
  colorCrosswalkCardsToJson,
  downloadColorCrosswalkCards,
  downloadOrderMapping,
  orderMappingGapsToCsv,
  orderMappingGapsToJson,
  type OrderMappingGap,
} from "@/lib/manufacturing/orderMappingExport";

const itemClass =
  "block w-full px-3 py-1.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-zinc-800";

export function InventoryOutlookOrderMappingExport({
  gaps,
  label = "Export",
}: {
  gaps: readonly OrderMappingGap[];
  label?: string;
}) {
  const [note, setNote] = useState<string | null>(null);
  const empty = gaps.length === 0;

  function closeMenu(event: { currentTarget: HTMLElement }) {
    event.currentTarget.closest("details")?.removeAttribute("open");
  }

  async function copy(format: "json" | "csv", event: { currentTarget: HTMLElement }) {
    const text = format === "json" ? orderMappingGapsToJson(gaps) : orderMappingGapsToCsv(gaps);
    closeMenu(event);
    try {
      await navigator.clipboard.writeText(text);
      setNote(`Copied ${format.toUpperCase()}`);
    } catch {
      setNote("Copy failed");
    }
  }

  return (
    <div className="flex items-center gap-2">
      <details className="relative">
        <summary
          className={
            "cursor-pointer list-none rounded-md border border-gray-300 bg-white px-2 py-1 text-sm font-semibold dark:border-zinc-600 dark:bg-zinc-900 [&::-webkit-details-marker]:hidden" +
            (empty ? " pointer-events-none opacity-50" : "")
          }
        >
          {label}
        </summary>
        {empty ? null : (
          <div className="absolute right-0 z-10 mt-1 w-40 rounded-md border border-gray-200 bg-white py-1 shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
            <button type="button" className={itemClass} onClick={(event) => void copy("json", event)}>
              Copy JSON
            </button>
            <button type="button" className={itemClass} onClick={(event) => void copy("csv", event)}>
              Copy CSV
            </button>
            <button
              type="button"
              className={itemClass}
              onClick={(event) => {
                downloadOrderMapping("json", gaps);
                closeMenu(event);
              }}
            >
              Download JSON
            </button>
            <button
              type="button"
              className={itemClass}
              onClick={(event) => {
                downloadOrderMapping("csv", gaps);
                closeMenu(event);
              }}
            >
              Download CSV
            </button>
          </div>
        )}
      </details>
      {note ? <span className="text-xs text-gray-500 dark:text-zinc-400">{note}</span> : null}
    </div>
  );
}

export function InventoryOutlookColorCardExport({ cards }: { cards: readonly ColorCrosswalkCard[] }) {
  const [note, setNote] = useState<string | null>(null);
  const empty = cards.length === 0;

  function closeMenu(event: { currentTarget: HTMLElement }) {
    event.currentTarget.closest("details")?.removeAttribute("open");
  }

  async function copy(format: "json" | "csv", event: { currentTarget: HTMLElement }) {
    const text = format === "json" ? colorCrosswalkCardsToJson(cards) : colorCrosswalkCardsToCsv(cards);
    closeMenu(event);
    try {
      await navigator.clipboard.writeText(text);
      setNote(`Copied color mappings ${format.toUpperCase()}`);
    } catch {
      setNote("Copy failed");
    }
  }

  return (
    <div className="flex items-center gap-2">
      <details className="relative">
        <summary
          className={
            "cursor-pointer list-none rounded-md border border-gray-300 bg-white px-2 py-1 text-sm font-semibold dark:border-zinc-600 dark:bg-zinc-900 [&::-webkit-details-marker]:hidden" +
            (empty ? " pointer-events-none opacity-50" : "")
          }
        >
          Export color mappings
        </summary>
        {empty ? null : (
          <div className="absolute right-0 z-10 mt-1 w-40 rounded-md border border-gray-200 bg-white py-1 shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
            <button type="button" className={itemClass} onClick={(event) => void copy("json", event)}>
              Copy JSON
            </button>
            <button type="button" className={itemClass} onClick={(event) => void copy("csv", event)}>
              Copy CSV
            </button>
            <button
              type="button"
              className={itemClass}
              onClick={(event) => {
                downloadColorCrosswalkCards("json", cards);
                closeMenu(event);
              }}
            >
              Download JSON
            </button>
            <button
              type="button"
              className={itemClass}
              onClick={(event) => {
                downloadColorCrosswalkCards("csv", cards);
                closeMenu(event);
              }}
            >
              Download CSV
            </button>
          </div>
        )}
      </details>
      {note ? <span className="text-xs text-gray-500 dark:text-zinc-400">{note}</span> : null}
    </div>
  );
}
